// Urlaub & Geburtstage — Dark.

import Card from "../Card";
import {
  BirthdayEntry,
  VacationEntry,
  VacationsResponse,
  formatDate,
} from "../../lib/api";

interface Props {
  data: VacationsResponse;
}

const FIRMA_BADGE: Record<string, string> = {
  DrainX: "bg-emerald-500/15 text-emerald-300",
  "G&K Sanitaertechnik": "bg-indigo-500/15 text-indigo-300",
  InfraX: "bg-rose-500/15 text-rose-300",
};

function firmaBadge(firma: string): string {
  return FIRMA_BADGE[firma] ?? "bg-slate-700/50 text-slate-400";
}

function range(v: VacationEntry): string {
  if (v.start_iso === v.end_iso) return formatDate(v.start_iso);
  return `${formatDate(v.start_iso)} – ${formatDate(v.end_iso)}`;
}

export default function VacationsCard({ data }: Props) {
  if (!data.available) {
    return (
      <Card title="Urlaub & Geburtstage" accent="neutral">
        <p className="text-slate-500">{data.setup_hint ?? "Nicht verfügbar."}</p>
      </Card>
    );
  }
  const total =
    data.today_off.length +
    data.this_week.length +
    data.coming_2_weeks.length +
    data.birthdays_14d.length;
  const subtitle = data.stale ? "⚠ Cache" : `${total} Einträge / 14 d`;

  return (
    <Card title="Urlaub & Geburtstage" subtitle={subtitle} accent="blue">
      {total === 0 && <p className="text-slate-500">Niemand im Urlaub.</p>}

      <Section title="Heute frei" emptyHint="Niemand">
        {data.today_off.map((v) => (
          <Row key={`tod-${v.name}-${v.start_iso}`}>
            <NameAndFirma name={v.name} firma={v.firma} />
            <span className="text-[11px] text-slate-500 tabular-nums">
              bis {formatDate(v.end_iso)}
            </span>
          </Row>
        ))}
      </Section>

      <Section title="Diese Woche" emptyHint="Nichts">
        {data.this_week.map((v) => (
          <Row key={`tw-${v.name}-${v.start_iso}`}>
            <NameAndFirma name={v.name} firma={v.firma} />
            <span className="text-[11px] text-slate-500 tabular-nums">{range(v)}</span>
          </Row>
        ))}
      </Section>

      <Section title="In 2 Wochen — vorgemerkt" emptyHint="Nichts">
        {data.coming_2_weeks.map((v) => (
          <Row key={`c2w-${v.name}-${v.start_iso}`}>
            <NameAndFirma name={v.name} firma={v.firma} />
            <span className="flex items-center gap-2 text-[11px] text-slate-500">
              {v.days_until_start !== null && (
                <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 tabular-nums">
                  in {v.days_until_start}d
                </span>
              )}
              <span className="tabular-nums">{range(v)}</span>
            </span>
          </Row>
        ))}
      </Section>

      <Section title="Geburtstage" emptyHint="Keine">
        {data.birthdays_14d.map((b) => (
          <Row key={`bd-${b.name}-${b.iso}`}>
            <span className="font-medium text-slate-200">🎂 {b.name}</span>
            <span className="text-[11px] text-slate-500 tabular-nums">
              {b.days_until === 0
                ? "heute"
                : b.days_until === 1
                  ? "morgen"
                  : `in ${b.days_until}d`}
            </span>
          </Row>
        ))}
      </Section>
    </Card>
  );
}

function Section({
  title,
  emptyHint,
  children,
}: {
  title: string;
  emptyHint: string;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const hasItems = items.some(Boolean);
  return (
    <div className="mt-3 first:mt-0">
      <h3 className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1">
        {title}
      </h3>
      {hasItems ? (
        <ul className="space-y-0.5">{children}</ul>
      ) : (
        <p className="text-[11px] text-slate-600 pl-1">{emptyHint}</p>
      )}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3 py-0.5">{children}</li>
  );
}

function NameAndFirma({ name, firma }: { name: string; firma: string }) {
  return (
    <span className="flex items-center gap-2 min-w-0">
      <span className="font-medium text-slate-200 truncate">{name}</span>
      {firma && (
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded ${firmaBadge(firma)} whitespace-nowrap`}
        >
          {firma === "G&K Sanitaertechnik" ? "G+K" : firma}
        </span>
      )}
    </span>
  );
}

export type { BirthdayEntry as _BirthdayEntry };
